import { useState } from 'react'
import { supabase } from '../lib/supabase'
import { useI18n } from '../i18n/useI18n'
import { Sheet } from '../components/Sheet'
import { TextField, TextAreaField, SelectField, DateField } from '../components/Fields'
import type { Unit, UnitKind } from '../lib/types'

/** Creation et modification d'une unite. */
export function UnitSheet({
  unit,
  onClose,
  onSaved,
}: {
  unit: Unit | null
  onClose: () => void
  onSaved: () => void
}) {
  const { t } = useI18n()

  const [name, setName] = useState(unit?.name ?? '')
  const [kind, setKind] = useState<UnitKind>(unit?.kind ?? 'habitation')
  const [floor, setFloor] = useState(unit?.floor ?? '')
  const [notes, setNotes] = useState(unit?.notes ?? '')
  const [vacantSince, setVacantSince] = useState(unit?.vacant_since ?? '')
  const [vacancyReason, setVacancyReason] = useState(unit?.vacancy_reason ?? '')

  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function submit() {
    setError(null)
    setSubmitting(true)

    const payload = {
      name: name.trim(),
      kind,
      floor: floor.trim() || null,
      notes: notes.trim() || null,
      vacant_since: vacantSince || null,
      vacancy_reason: vacancyReason.trim() || null,
    }

    const { error: e } = unit
      ? await supabase.from('units').update(payload).eq('id', unit.id)
      : await supabase.from('units').insert(payload)

    setSubmitting(false)
    if (e) {
      // On garde la saisie a l'ecran : rien n'est perdu.
      setError(`${t('save_failed')} ${e.message}`)
      return
    }
    onSaved()
  }

  return (
    <Sheet
      title={unit ? t('unit_edit') : t('unit_add')}
      onClose={onClose}
      onSubmit={submit}
      submitLabel={t('save')}
      submitting={submitting}
      disabled={name.trim().length === 0}
      error={error}
    >
      <TextField
        label={t('unit_name')}
        hint={t('unit_name_hint')}
        required
        value={name}
        onChange={setName}
      />

      <SelectField<UnitKind>
        label={t('unit_kind')}
        required
        value={kind}
        onChange={setKind}
        options={[
          { value: 'habitation', label: t('unit_kind_habitation') },
          { value: 'commercial', label: t('unit_kind_commercial') },
        ]}
      />

      <TextField
        label={t('unit_floor')}
        hint={t('optional')}
        value={floor}
        onChange={setFloor}
        autoCapitalize="none"
      />

      <DateField
        label={t('unit_vacant_since_label')}
        hint={t('unit_vacancy_hint')}
        value={vacantSince}
        onChange={setVacantSince}
      />

      {vacantSince && (
        <TextField
          label={t('unit_vacancy_reason')}
          value={vacancyReason}
          onChange={setVacancyReason}
          autoCapitalize="sentences"
        />
      )}

      <TextAreaField
        label={t('unit_notes')}
        hint={t('optional')}
        value={notes}
        onChange={setNotes}
      />
    </Sheet>
  )
}
